import React, { Fragment, useState, useRef, RefObject, useEffect, createRef, ReactElement, useContext } from "react";
import cn from "classnames";
import { indexOfSmallest } from "../tools";
import Draggable from "./Draggable";
import { SettingsContext } from "../SettingsProvider";
import IconDrag from "./IconDrag";

import styles from "./Statuses.module.scss";

interface Props {
    selectedLabelIds: Set<string>;
    setSelectedLabelIds: (newSelectedLabelIds: Set<string>) => void;
}

const Statuses: React.FC<Props> = function ({ selectedLabelIds, setSelectedLabelIds }: Props) {
    const { labels, setLabels } = useContext(SettingsContext);
    const containerRef = useRef<HTMLDivElement>(null);
    const [statusRefs, setStatusRefs] = useState<RefObject<HTMLDivElement>[]>([]);

    const [draggedStatusId, setDraggedStatusId] = useState<string>();
    const [draggedStatusSlot, setDraggedStatusSlot] = useState<number>();

    useEffect(() => {
        setStatusRefs((statusRefs) =>
            Array(labels.orderedIds.length + 1) // +1 for dummy div at the end to allow dropping after last item
                .fill(undefined)
                .map((_, i) => statusRefs[i] || createRef())
        );
    }, [labels]);

    const toggleSelectedLabelId = (labelId: string): void => {
        const newSet = new Set(selectedLabelIds);
        if (selectedLabelIds.has(labelId)) {
            newSet.delete(labelId);
        } else {
            newSet.add(labelId);
        }
        setSelectedLabelIds(newSet);
    };

    const handleDragStart = (labelId: string) => ({ clientX }: any): void => {
        setDraggedStatusId(labelId);
    };

    const handleDragEnd = (): void => {
        if (draggedStatusId !== undefined && draggedStatusSlot !== undefined) {
            const idx = labels.orderedIds.indexOf(draggedStatusId);
            if (idx !== draggedStatusSlot) {
                const newOrderedIds = [...labels.orderedIds];
                // remove from old position
                newOrderedIds.splice(idx, 1);
                // insert in new position
                const newIdx = draggedStatusSlot > idx ? draggedStatusSlot - 1 : draggedStatusSlot;
                newOrderedIds.splice(newIdx, 0, draggedStatusId);
                setLabels({ orderedIds: newOrderedIds });
            }
        }
        setDraggedStatusId(undefined);
        setDraggedStatusSlot(undefined);
    };

    const getClosestSlot = (x: number, y: number): number => {
        const rects = statusRefs.map((ref) => (ref.current ? ref.current.getBoundingClientRect() : null));
        // hack: make y distance much bigger so that item doesn't jump between rows abruptly
        const dists = rects.map((rect) =>
            rect ? (rect.left - x) * (rect.left - x) + (rect.top - y) * (rect.top - y) * 100 : 1e5
        );
        return indexOfSmallest(dists);
    };

    const handleDrag = ({ clientX, clientY }: any): void => {
        const newSlot = getClosestSlot(clientX, clientY);
        if (newSlot !== draggedStatusSlot) {
            setDraggedStatusSlot(newSlot);
        }
    };

    const getBoundingRect = (): DOMRect | null =>
        containerRef.current ? containerRef.current.getBoundingClientRect() : null;
    const boundingRect = getBoundingRect();

    const statusElement = (labelId: string, shadow: boolean): ReactElement => {
        const style = labels.labelsColors[labelId];
        const selected = selectedLabelIds.has(labelId);
        return (
            <div
                className={cn(styles.label, selected ? styles.selected : styles.unselected, {
                    [styles.shadow]: shadow
                })}
                onClick={(): void => toggleSelectedLabelId(labelId)}
            >
                <IconDrag className={styles.dragIcon} />
                <div
                    className={styles.labelMarker}
                    style={{
                        backgroundColor: style.color
                    }}
                />
                <span className={styles.labelName}>{labels.names[labelId]}</span>
            </div>
        );
    };

    return (
        <div className={styles.labels} ref={containerRef}>
            {labels.orderedIds.map((labelId, idx) => {
                return (
                    <Fragment key={labelId}>
                        {draggedStatusSlot === idx && draggedStatusId && statusElement(draggedStatusId, true)}
                        <Draggable
                            ref={statusRefs[idx]}
                            bounceBack
                            onDragStart={handleDragStart(labelId)}
                            onDragEnd={handleDragEnd}
                            onDrag={handleDrag}
                            minX={boundingRect ? boundingRect.left : 0}
                            maxX={boundingRect ? boundingRect.right : 0}
                            minY={boundingRect ? boundingRect.top : 0}
                            maxY={boundingRect ? boundingRect.bottom : 0}
                            dragDelayDist={100}
                        >
                            {statusElement(labelId, false)}
                        </Draggable>
                    </Fragment>
                );
            })}
            {draggedStatusSlot === labels.orderedIds.length && draggedStatusId && statusElement(draggedStatusId, true)}
            <div className={styles.dummy} ref={statusRefs[labels.orderedIds.length]} />
            <div
                className={styles.resetButton}
                onClick={(): void => {
                    setLabels(null);
                }}
            >
                reset order
            </div>
        </div>
    );
};

export default Statuses;
